/**
 * ENT-3704: platform-standard "open settings" keyboard shortcut — `Cmd+,` on
 * macOS, `Ctrl+,` on Windows/Linux — plus the guards that keep it from firing
 * while the user is typing or a dialog owns the foreground. Kept as pure,
 * DOM-only helpers so the keydown listener (AppCommandPalette) and the avatar
 * dropdown hint stay thin and unit-testable.
 */

/** True when the current platform is macOS-like (Cmd is the primary modifier). */
export function isMacPlatform(): boolean {
  if (typeof navigator === 'undefined') {
    return false;
  }

  const platform = navigator.platform || '';
  const userAgent = navigator.userAgent || '';
  return /Mac|iPhone|iPod|iPad/i.test(platform) || /Mac OS X/i.test(userAgent);
}

/** Human-readable hint for the settings shortcut, e.g. `⌘,` or `Ctrl+,`. */
export function getSettingsShortcutHint(): string {
  return isMacPlatform() ? '⌘,' : 'Ctrl+,';
}

/**
 * True when the event is the settings shortcut: the platform command modifier
 * (Cmd on macOS, Ctrl elsewhere — either is accepted, matching the ⌘K palette
 * listener) plus the comma character, with no Alt/Shift qualifier.
 *
 * Matches on `event.key` (the layout-aware produced character), not
 * `event.code`: `event.code === 'Comma'` is the PHYSICAL US-comma key position,
 * which on Dvorak/AZERTY/etc. produces a different character — pressing Cmd with
 * that key must NOT be mistaken for the shortcut. The `code` path is kept only
 * as a fallback for genuinely unidentified keys (some IME/synthetic events).
 */
export function isSettingsShortcutEvent(event: KeyboardEvent): boolean {
  if (event.altKey || event.shiftKey) {
    return false;
  }

  if (!event.metaKey && !event.ctrlKey) {
    return false;
  }

  if (event.key === ',') {
    return true;
  }

  const keyIsUnidentified = event.key === 'Unidentified' || event.key === '';
  return keyIsUnidentified && event.code === 'Comma';
}

/**
 * True when focus is in a text-entry surface (input/textarea/select/
 * contenteditable/textbox), where a shortcut must defer to typing.
 */
export function isEditableEventTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) {
    return false;
  }

  const tagName = target.tagName;
  if (tagName === 'INPUT' || tagName === 'TEXTAREA' || tagName === 'SELECT') {
    return true;
  }

  if (target.isContentEditable) {
    return true;
  }

  const role = target.getAttribute('role');
  return role === 'textbox' || role === 'searchbox';
}

/**
 * True when the shortcut fired from within a text-entry surface, inspecting the
 * event's composed path rather than just `event.target`.
 *
 * A keydown inside a shadow-root editor (e.g. the Sentry feedback widget, which
 * lives in a `ShadowRoot`) is retargeted to the non-editable shadow host by the
 * time it bubbles to `window`, so `event.target` alone reports "not editable"
 * and the shortcut would navigate away and discard the draft. `composedPath()`
 * still contains the real editable control, so walk it. Falls back to
 * `event.target` when `composedPath` is unavailable (older/synthetic events).
 */
export function isEditableEventContext(event: Event): boolean {
  const path =
    typeof event.composedPath === 'function' ? event.composedPath() : null;

  if (path && path.length > 0) {
    return path.some((node) => isEditableEventTarget(node));
  }

  return isEditableEventTarget(event.target);
}

/**
 * True when a modal dialog layer is present (the command palette itself, a
 * drawer, an alert dialog). Radix keeps dialog content in the DOM only while
 * open, so presence — minus explicitly hidden nodes — is the open signal. No
 * layout/rect check so the guard behaves identically under jsdom.
 */
export function hasOpenDialogLayer(): boolean {
  if (typeof document === 'undefined') {
    return false;
  }

  return Array.from(
    document.querySelectorAll('[role="dialog"], [role="alertdialog"]'),
  ).some((element) => {
    if (element.getAttribute('aria-hidden') === 'true') {
      return false;
    }
    return !(element instanceof HTMLElement && element.hidden);
  });
}

/**
 * Combined verdict for the settings shortcut: the key combo matched AND focus
 * is not in a text field AND no dialog owns the foreground.
 */
export function shouldOpenSettingsFromShortcut(event: KeyboardEvent): boolean {
  return (
    isSettingsShortcutEvent(event) &&
    !isEditableEventContext(event) &&
    !hasOpenDialogLayer()
  );
}
